import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { StatusBadge, PriorityBadge, TypeTag } from '../../components/ui';
import { timeAgo } from '../../lib/format';
import { Icon, type IconName } from '../../lib/icons';
import { getRecentTickets } from '../../lib/recents';
import {
  useQuickSearch, useSearchHistory, trackSearchClick, type SearchResult,
} from './api';

/**
 * Global search (header): quick-search dropdown with debounced instant
 * results, recent items, keyboard navigation and highlighted snippets.
 * Activation: click, `/`, or Ctrl/Cmd+K from anywhere.
 */

const DEBOUNCE_MS = 250;

/** XSS-safe term highlighter: renders text nodes + <mark>, never raw HTML. */
export function Highlight({ text, terms }: { text: string; terms: string[] }) {
  const clean = terms.map((t) => t.trim()).filter((t) => t.length > 0);
  if (!clean.length || !text) return <>{text}</>;
  const pattern = clean
    .sort((a, b) => b.length - a.length)
    .map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('|');
  let re: RegExp;
  try {
    re = new RegExp(`(${pattern})`, 'ig');
  } catch {
    return <>{text}</>;
  }
  const parts = text.split(re);
  return (
    <>
      {parts.map((p, i) =>
        i % 2 === 1 ? (
          <mark key={i} className="bg-amber-200/70 dark:bg-amber-500/30 text-inherit rounded-sm px-0">
            {p}
          </mark>
        ) : (
          <span key={i}>{p}</span>
        ),
      )}
    </>
  );
}

const SOURCE_LABEL: Record<string, string> = {
  key: 'Key', title: 'Title', description: 'Description', comments: 'Comment',
  tags: 'Tag', customer: 'Customer', hubspot: 'HubSpot', attachments: 'Attachment',
  sprint_release: 'Sprint/Release', doc_page: 'Doc', doc_document: 'Doc',
  comment: 'Comment', attachment: 'Attachment', sprint: 'Sprint', release: 'Release',
  roadmap_item: 'Roadmap', user: 'User', tag: 'Tag', customer_entity: 'Customer', other: 'Match',
};

const ENTITY_ICON: Record<string, IconName> = {
  ticket: 'ticket', comment: 'comment', doc_page: 'document', doc_document: 'document',
  attachment: 'attach', sprint: 'sprint', release: 'release',
  roadmap_item: 'flag', user: 'user', tag: 'list', customer: 'customer',
};

type Row =
  | { kind: 'result'; result: SearchResult }
  | { kind: 'recent-ticket'; id: string; code: string; title: string }
  | { kind: 'recent-query'; query: string }
  | { kind: 'aql'; query: string }
  | { kind: 'all'; query: string };

export function GlobalSearch() {
  const nav = useNavigate();
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [q]);

  const looksAql = /[a-z_]+\s*(=|!=|~|>=|<=)\s*/i.test(q) && /["'()]|\b(AND|OR|IN)\b/i.test(q);
  const { data, isFetching } = useQuickSearch(debounced, open && !looksAql);
  const history = useSearchHistory(open && q.trim() === '');
  const recents = useMemo(() => getRecentTickets(), [open]);

  // Global activation: `/` or Ctrl/Cmd+K.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
        return;
      }
      if (e.key === '/' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        const el = e.target as HTMLElement | null;
        if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return;
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [open]);

  const terms = useMemo(
    () => debounced.replace(/"/g, ' ').split(/\s+/).filter((t) => t.length > 1 && !t.includes(':')),
    [debounced],
  );

  // Flatten the visible rows for keyboard navigation.
  const rows: Row[] = useMemo(() => {
    const out: Row[] = [];
    const trimmed = q.trim();
    if (!trimmed) {
      for (const r of recents.slice(0, 6)) out.push({ kind: 'recent-ticket', id: r.id, code: r.code, title: r.title });
      for (const h of history.data?.items.slice(0, 5) ?? []) out.push({ kind: 'recent-query', query: h.queryText });
      return out;
    }
    if (looksAql) {
      out.push({ kind: 'aql', query: trimmed });
      return out;
    }
    if (data && data.query === debounced) {
      const seen = new Set<string>();
      for (const r of [...data.tickets, ...data.docs, ...data.comments, ...data.entities]) {
        if (seen.has(r.id)) continue;
        seen.add(r.id);
        out.push({ kind: 'result', result: r });
      }
    }
    out.push({ kind: 'all', query: trimmed });
    return out;
  }, [q, debounced, data, looksAql, recents, history.data]);

  useEffect(() => setActive(0), [rows.length, debounced]);

  const openRow = (row: Row, newTab = false) => {
    const go = (url: string) => {
      setOpen(false);
      setQ('');
      if (newTab) window.open(url, '_blank');
      else nav(url);
    };
    switch (row.kind) {
      case 'result':
        trackSearchClick(debounced, row.result);
        go(row.result.url);
        break;
      case 'recent-ticket':
        go(`/tickets/${row.code}`);
        break;
      case 'recent-query':
        setQ(row.query);
        setDebounced(row.query);
        break;
      case 'aql':
      case 'all':
        go(`/search?q=${encodeURIComponent(row.query)}`);
        break;
    }
  };

  const onInputKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false);
      inputRef.current?.blur();
      return;
    }
    if (!open) setOpen(true);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, rows.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const row = rows[active] ?? rows[rows.length - 1];
      if (row) openRow(row, e.ctrlKey || e.metaKey);
      else if (q.trim()) openRow({ kind: 'all', query: q.trim() });
    }
  };

  // Keep the active row visible.
  useEffect(() => {
    listRef.current
      ?.querySelector<HTMLElement>(`[data-row-idx="${active}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const sections = useMemo(() => {
    // Group consecutive rows into labeled sections for rendering.
    const out: { label: string | null; rows: { row: Row; idx: number }[] }[] = [];
    const labelOf = (r: Row): string | null => {
      switch (r.kind) {
        case 'recent-ticket': return 'Recently opened';
        case 'recent-query': return 'Recent searches';
        case 'result': {
          const t = r.result.entityType;
          if (t === 'ticket') return 'Tickets';
          if (t === 'doc_page' || t === 'doc_document') return 'Docs';
          if (t === 'comment') return 'Comments';
          return 'More';
        }
        default: return null;
      }
    };
    rows.forEach((row, idx) => {
      const label = labelOf(row);
      const last = out[out.length - 1];
      if (last && last.label === label) last.rows.push({ row, idx });
      else out.push({ label, rows: [{ row, idx }] });
    });
    return out;
  }, [rows]);

  return (
    <div ref={boxRef} className="global-search flex-1 max-w-xl mx-auto relative">
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none">
          <Icon name="search" />
        </span>
        <input
          ref={inputRef}
          className="input w-full !pl-9 pr-3 sm:pr-16 bg-surface2/60"
          placeholder="Search tickets, docs, comments, users…"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onInputKey}
          aria-label="Global search"
          aria-expanded={open}
          role="combobox"
          aria-controls="global-search-results"
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 hidden sm:flex items-center gap-1 text-[10px] text-muted pointer-events-none">
          <kbd className="border border-border rounded px-1 py-0.5 bg-surface2">Ctrl</kbd>
          <kbd className="border border-border rounded px-1 py-0.5 bg-surface2">K</kbd>
        </span>
      </div>

      {open && (q.trim() || rows.length > 0) && (
        <div
          id="global-search-results"
          className="absolute left-0 right-0 top-full mt-2 card shadow-pop z-50 overflow-hidden"
        >
          <div ref={listRef} className="max-h-[65vh] overflow-y-auto py-1">
            {looksAql && (
              <div className="px-3 py-2 text-xs text-muted border-b border-border">
                <Icon name="ai" /> AQL detected — press Enter to run it in full search.
              </div>
            )}
            {sections.map((s, si) => (
              <div key={si}>
                {s.label && (
                  <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wide text-muted">{s.label}</div>
                )}
                {s.rows.map(({ row, idx }) => (
                  <RowView
                    key={idx}
                    row={row}
                    idx={idx}
                    active={idx === active}
                    terms={terms}
                    onOpen={(newTab) => openRow(row, newTab)}
                    onHover={() => setActive(idx)}
                  />
                ))}
              </div>
            ))}
            {q.trim().length >= 2 && !looksAql && data && data.query === debounced &&
              data.totalEstimated === 0 && (
                <div className="px-3 py-4 text-sm text-muted text-center">
                  No matching results
                  {data.didYouMean && (
                    <div className="mt-1">
                      Did you mean{' '}
                      <button
                        className="text-brand hover:underline"
                        onMouseDown={(e) => { e.preventDefault(); setQ(data.didYouMean!); }}
                      >
                        {data.didYouMean}
                      </button>
                      ?
                    </div>
                  )}
                </div>
              )}
            {q.trim().length >= 2 && !looksAql && (!data || data.query !== debounced) && isFetching && (
              <div className="px-3 py-3 text-sm text-muted">Searching…</div>
            )}
          </div>
          <div className="flex items-center justify-between px-3 py-1.5 border-t border-border text-[10px] text-muted bg-surface2/50">
            <span>↑↓ navigate · Enter open · Ctrl+Enter new tab · Esc close</span>
            {data && data.query === debounced && q.trim().length >= 2 && (
              <span>{data.totalEstimated} result{data.totalEstimated === 1 ? '' : 's'} · {data.tookMs}ms</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function RowView({ row, idx, active, terms, onOpen, onHover }: {
  row: Row; idx: number; active: boolean; terms: string[];
  onOpen: (newTab: boolean) => void; onHover: () => void;
}) {
  const base = `block w-full text-left px-3 py-2 cursor-pointer ${active ? 'bg-surface2' : 'hover:bg-surface2/60'}`;
  const wrap = (children: ReactNode) => (
    <div
      data-row-idx={idx}
      className={base}
      onMouseEnter={onHover}
      onMouseDown={(e) => { e.preventDefault(); onOpen(e.ctrlKey || e.metaKey); }}
    >
      {children}
    </div>
  );

  if (row.kind === 'recent-ticket') {
    return wrap(
      <div className="flex items-center gap-2 text-sm">
        <span className="text-muted"><Icon name="recent" /></span>
        <span className="font-mono text-xs text-muted">{row.code}</span>
        <span className="truncate">{row.title}</span>
      </div>,
    );
  }
  if (row.kind === 'recent-query') {
    return wrap(
      <div className="flex items-center gap-2 text-sm text-muted">
        <Icon name="search" />
        <span className="truncate">{row.query}</span>
      </div>,
    );
  }
  if (row.kind === 'aql') {
    return wrap(
      <div className="flex items-center gap-2 text-sm">
        <Icon name="ai" />
        <span>Run AQL: <span className="font-mono text-xs">{row.query}</span></span>
      </div>,
    );
  }
  if (row.kind === 'all') {
    return wrap(
      <div className="flex items-center gap-2 text-sm text-brand font-medium">
        <Icon name="search" />
        <span>See all results for “{row.query}”</span>
      </div>,
    );
  }

  const r = row.result;
  const isTicket = r.entityType === 'ticket';
  const snippet = r.highlights[0]?.snippet;
  const matchedIn = r.matchedFields[0];
  return wrap(
    <div className="min-w-0">
      <div className="flex items-center gap-2 min-w-0">
        <span className="text-muted shrink-0"><Icon name={ENTITY_ICON[r.entityType] ?? 'search'} /></span>
        {r.key && <span className="font-mono text-xs text-muted shrink-0">{r.key}</span>}
        <span className="truncate text-sm font-medium">
          <Highlight text={r.title} terms={terms} />
        </span>
        <span className="ml-auto shrink-0 text-[10px] text-muted">{timeAgo(r.updatedAt)}</span>
      </div>
      <div className="flex items-center gap-1.5 mt-1 flex-wrap">
        {isTicket && r.ticketType && <TypeTag type={r.ticketType} />}
        {r.status && <StatusBadge status={r.status} />}
        {isTicket && r.priority && <PriorityBadge priority={r.priority} />}
        {matchedIn && (
          <span className="chip bg-surface2 text-muted" title={`Matched in ${r.matchedFields.join(', ')}`}>
            in {SOURCE_LABEL[matchedIn] ?? matchedIn}
          </span>
        )}
        {(r.metadata.commentMatchCount ?? 0) > 0 && (
          <span className="text-[10px] text-muted">{r.metadata.commentMatchCount} comment match{(r.metadata.commentMatchCount ?? 0) > 1 ? 'es' : ''}</span>
        )}
      </div>
      {snippet && (
        <div className="text-xs text-muted mt-1 truncate">
          <Highlight text={snippet} terms={terms} />
        </div>
      )}
    </div>,
  );
}
